'use client';

import Link from 'next/link'
import {
  ArrowRight, BarChart3, Lock, Zap, Globe, Link2, User, LogOut,
  QrCode, MousePointerClick, MapPin, Smartphone, LayoutTemplate,
  TrendingUp, Shield, Menu, X, Gift, ShoppingBag, FormInput, MessageSquare, ChevronDown,
  LayoutDashboard, BarChart2, MousePointer2, Sparkles, Plus, Search, MoreVertical, Copy, Link as LinkIcon, Instagram, Twitter, Linkedin, Github, Film
} from 'lucide-react'
import { motion, Variants, AnimatePresence } from 'framer-motion'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { useEffect, useState, useRef } from 'react'
import { PRICING_CONFIG } from '@/lib/pricing-config'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { ContainerScroll } from '@/components/ui/container-scroll-animation'
import { GlowingEffect } from '@/components/ui/glowing-effect'
import Image from 'next/image'
import dynamic from 'next/dynamic'

const DisplayCards = dynamic(() => import('@/components/ui/display-cards'), {
  ssr: false,
  loading: () => <div className="h-[400px] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />,
})

const FlickeringGrid = dynamic(() => import('@/components/ui/flickering-grid').then(mod => mod.FlickeringGrid), {
  ssr: false,
})

const DashboardAnimation = dynamic(() => import('@/components/ui/dashboard-animation').then(mod => mod.DashboardAnimation), {
  ssr: false,
  loading: () => <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />,
})

const DashboardPreview = dynamic(() => import('@/components/ui/dashboard-animation').then(mod => mod.DashboardPreview), {
  ssr: false,
  loading: () => <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl" />,
})

const GlobeDemo = dynamic(() => import('@/components/globe-demo'), {
  ssr: false,
  loading: () => <div className="h-[32rem] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />,
})

const TextMarquee = dynamic(() => import('@/components/ui/text-marquee').then(mod => mod.TextMarquee), {
  ssr: false,
})

const StackedHowItWorks = dynamic(() => import('@/components/sections/how-it-works-stacked').then(mod => mod.StackedHowItWorks), {
  ssr: false,
  loading: () => <div className="h-96 w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />,
})

const ShieldPulse = ({ isMobile }: { isMobile?: boolean }) => (
  <div className="relative w-full h-full flex items-center justify-center">
    {!isMobile && (
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
        style={{ willChange: 'transform' }}
        className="absolute inset-0 bg-primary/20 rounded-full"
      />
    )}
    <Shield className="w-4 h-4 text-primary relative z-10" />
  </div>
)

const RadarPing = ({ isMobile }: { isMobile?: boolean }) => (
  <div className="relative w-full h-full flex items-center justify-center">
    {!isMobile && [0, 1].map((i) => (
      <motion.div
        key={i}
        initial={{ scale: 0.5, opacity: 0.8 }}
        animate={{ scale: 2, opacity: 0 }}
        transition={{ duration: 2, repeat: Infinity, delay: i * 1 }}
        style={{ willChange: 'transform' }}
        className="absolute inset-0 border border-primary/40 rounded-full"
      />
    ))}
    <MapPin className="w-4 h-4 text-primary relative z-10" />
  </div>
)

const DeviceToggle = ({ isMobile }: { isMobile?: boolean }) => (
  <div className="relative w-full h-full flex items-center justify-center">
    <motion.div
      animate={isMobile ? { rotateY: 0 } : { rotateY: [0, 180, 360] }}
      transition={{ duration: 4, repeat: isMobile ? 0 : Infinity, ease: "easeInOut" }}
      style={{ willChange: 'transform' }}
      className="relative w-4 h-4"
    >
      <Smartphone className="w-4 h-4 text-primary" />
    </motion.div>
  </div>
)

const SparklinePeak = ({ isMobile }: { isMobile?: boolean }) => (
  <div className="relative w-full h-full flex items-end justify-center gap-0.5 px-1">
    {[30, 50, 80, 40].map((h, i) => (
      <motion.div
        key={i}
        className="w-1 bg-primary/40 rounded-t-sm"
        style={{ height: `${h}%`, transformOrigin: "bottom", willChange: "transform" }}
        animate={isMobile ? { scaleY: 1 } : { scaleY: [1, 1.25, 1] }}
        transition={{ duration: 2, repeat: isMobile ? 0 : Infinity, delay: i * 0.2 }}
      />
    ))}
    <TrendingUp className="absolute top-0 right-0 w-3 h-3 text-primary opacity-50" />
  </div>
)

export default function HomeClient() {
  const [session, setSession] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined)
  const [isSouthAsia, setIsSouthAsia] = useState(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)

  const [displayCardsInView, setDisplayCardsInView] = useState(false)
  const [howItWorksInView, setHowItWorksInView] = useState(false)
  const [featuresInView, setFeaturesInView] = useState(false)
  const [globeInView, setGlobeInView] = useState(false)

  const displayCardsRef = useRef<HTMLDivElement>(null)
  const howItWorksRef = useRef<HTMLDivElement>(null)
  const featuresRef = useRef<HTMLDivElement>(null)
  const globeRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Fetch auth status without blocking the initial render
    fetch('/api/auth/status')
      .then(res => res.json())
      .then(data => setSession(data.isSignedIn))
      .catch(() => {});

    // Detect South Asia from timezone (most reliable client-side signal)
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      const lang = navigator.language || '';
      
      const saTimezones = ['Asia/Kolkata', 'Asia/Calcutta', 'Asia/Karachi', 'Asia/Dhaka', 'Asia/Kathmandu', 'Asia/Colombo', 'Asia/Thimphu', 'Asia/Maldives'];
      const saLangPrefixes = ['hi', 'bn', 'te', 'mr', 'ta', 'gu', 'kn', 'ml', 'pa', 'ur'];
      
      const isTimezoneMatch = saTimezones.some(z => tz.includes(z));
      const isLangMatch = lang.includes('-IN') || lang.includes('-PK') || lang.includes('-BD') || 
                          saLangPrefixes.some(p => lang.startsWith(p));
      
      if (isTimezoneMatch || isLangMatch) {
        setIsSouthAsia(true);
      }
    } catch { }

    // IP geolocation as backup (only upgrades to SA, never downgrades)
    fetch('/api/country-code')
      .then(res => res.ok ? res.text() : Promise.reject())
      .then(code => {
        const saCountries = ['IN', 'PK', 'BD', 'LK', 'NP', 'BT', 'MV'];
        if (saCountries.includes(code.trim())) {
          setIsSouthAsia(true);
        }
      })
      .catch(() => {});

    const checkMobile = () => setIsMobile(window.innerWidth < 1024)
    checkMobile()
    window.addEventListener('resize', checkMobile)
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  useEffect(() => {
    const displayObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setDisplayCardsInView(true);
        displayObserver.disconnect();
      }
    }, { rootMargin: '250px' });

    if (displayCardsRef.current) {
      displayObserver.observe(displayCardsRef.current);
    }
    return () => displayObserver.disconnect();
  }, []);

  useEffect(() => {
    const howItWorksObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setHowItWorksInView(true);
        howItWorksObserver.disconnect();
      }
    }, { rootMargin: '250px' });

    if (howItWorksRef.current) {
      howItWorksObserver.observe(howItWorksRef.current);
    }
    return () => howItWorksObserver.disconnect();
  }, []);

  useEffect(() => {
    const featuresObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setFeaturesInView(true);
        featuresObserver.disconnect();
      }
    }, { rootMargin: '400px' });

    if (featuresRef.current) {
      featuresObserver.observe(featuresRef.current);
    }
    return () => featuresObserver.disconnect();
  }, []);

  useEffect(() => {
    const globeObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setGlobeInView(true);
        globeObserver.disconnect();
      }
    }, { rootMargin: '600px' });

    if (globeRef.current) {
      globeObserver.observe(globeRef.current);
    }
    return () => globeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (session) {
      fetch('/api/usage')
        .then(res => res.json())
        .then(data => setUserPlan(data.plan))
        .catch(() => console.error('Failed to fetch plan status'));
    } else {
      setUserPlan(null);
    }
  }, [session]);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0 } }
  }
  const itemVariants: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30" style={{ position: 'relative' }}>
      <GeometricBackground />

      <MarketingNavbar isSignedIn={session} />

      <main id="main-content">
        {/* ── Hero with Scroll Animation ── */}
      <section className="relative pt-0 pb-4 md:pb-10 min-h-[60vh] md:min-h-[80vh] flex flex-col items-center justify-start mt-0" style={{ position: 'relative' }}>
        <FlickeringGrid
          className="absolute inset-0 z-0 [mask-image:radial-gradient(800px_circle_at_center,white,transparent)]"
          squareSize={4}
          gridGap={6}
          color="#3b82f6"
          maxOpacity={0.5}
          flickerChance={0.2}
        />
        <ContainerScroll
          titleComponent={null}
        >
          <div className="h-full w-full grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-center p-6 md:p-8">
            {/* Left — Hero Content */}
            <div
              className="lg:col-span-8 space-y-6 flex flex-col items-center text-center"
            >
              {/* Eyebrow text centered horizontally */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="flex items-center gap-2 sm:gap-4 w-full justify-center"
              >
                <div className="h-px flex-1 bg-gradient-to-r from-transparent to-primary/60 hidden sm:block" />
                <span className="text-primary/80 text-[10px] sm:text-xs font-semibold tracking-[0.2em] uppercase whitespace-nowrap">
                  Marketing &amp; Branding Ecosystem
                </span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent to-primary/60 hidden sm:block" />
              </motion.div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tighter text-white leading-[1.1] uppercase">
                Build your brand.<br />
                <span className="text-gray-400">Scale your reach.</span><br />
                Own your ecosystem.
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                className="text-[13px] sm:text-[14px] text-white/50 max-w-xl font-light tracking-wide leading-relaxed"
              >
                Lenklyst is a unified marketing ecosystem for creators, startups, and businesses.
                From smart links and <b>link in bio &amp; reels to link</b> to <b>referral systems, product stores, and interactive forms</b>—everything you need to manage your entire presence in one platform.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 w-full sm:w-auto"
              >
                <Link
                  id="hero-cta-btn"
                  href={session ? '/dashboard' : '/auth/sign-up'}
                  className="group w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 bg-primary text-white rounded-full font-bold text-sm sm:text-base hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_40px_rgba(0,102,255,0.4)]"
                >
                  {session ? 'Go to Dashboard' : 'Start for Free'}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <a
                  id="hero-features-btn"
                  href="#features"
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-3.5 rounded-full font-bold text-sm sm:text-base text-white/60 border border-white/10 hover:border-white/20 hover:text-white transition-all"
                >
                  See Features
                </a>
              </motion.div>
            </div>

            {/* Right — animated dashboard visualizer */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
              className="lg:col-span-4 hidden lg:block"
            >
              <DashboardAnimation />
            </motion.div>
          </div>
        </ContainerScroll>
      </section>
      {/* ── Features ── */}
      <section id="features" ref={featuresRef} className="relative pt-6 pb-12 z-10 border-t border-white/5 overflow-hidden">
        {/* Ambient Glows */}
        <div className="absolute top-0 left-[-10%] w-[50vw] h-[50vw] bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.08),transparent_70%)] blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-[-10%] w-[40vw] h-[40vw] bg-[radial-gradient(circle_at_center,rgba(0,64,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
          <div className="mb-8">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tighter text-white mb-2 uppercase">
              Everything you need.
            </h2>
            <p className="text-xl text-white/50 max-w-2xl font-light">
              One platform to own your online presence from the first click to the last conversion.
            </p>
          </div>

          {/* Netflix Style Zig-Zag Rows */}
          <div className="space-y-1 sm:space-y-0">
            {/* Divider */}
            <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent my-4 hidden sm:block" />

            {/* Feature 1: Smart Links */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-1"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Smart<br /><span className="text-primary">links.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Shorten, brand, schedule, and protect any URL with advanced tracking.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="links" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 2: Viral Referrals */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-2 lg:pl-12"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Referral<br /><span className="text-primary">system.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Build a powerful referral engine that turns users into advocates and drives organic growth.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="referrals" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 3: Link in Bio & Reels */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-1"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Link in bio &amp;<br /><span className="text-primary">reels to link.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  A sleek bio hub for all your content. Pair reels with #codes and thumbnails so followers find any link in one tap.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <span className="text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/70 font-mono">#code search</span>
                  <span className="text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/70 font-mono">reels grid</span>
                  <span className="text-xs px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-white/70 font-mono">instant link</span>
                </div>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="bio" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 4: Product Stores */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-2 lg:order-2 lg:pl-8"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Digital<br /><span className="text-primary">stores.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Sell digital products and services directly from your branded storefront.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="products" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 5: Forms & Polls */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-1"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Forms &amp;<br /><span className="text-primary">polls.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Collect leads and gather feedback with deep analytics that track when and where responses originate.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="forms" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 6: Smart QR */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6 border-b border-white/5">
              <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-2 lg:pl-12"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Smart<br /><span className="text-primary">QR codes.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Connect physical interactions to your digital ecosystem.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="qr" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature 7: Team Collaboration */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center py-4 sm:py-6">
              <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="space-y-4 lg:order-1"
              >
                <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white uppercase">
                  Team<br /><span className="text-primary">collaboration.</span>
                </h3>
                <p className="text-lg text-white/50 font-light leading-relaxed max-w-xl">
                  Manage your team and coordinate projects with ease.
                </p>
              </motion.div>
              <div className="h-48 sm:h-56 lg:h-64 flex items-center justify-center lg:order-2">
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[380px]"
                >
                  {featuresInView ? (
                    <DashboardPreview sectionId="team" className="shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  ) : (
                    <div className="aspect-[1.6/1] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-xl shadow-[0_0_50px_rgba(59,130,246,0.15)]" />
                  )}
                </motion.div>
              </div>
            </div>

            {/* Feature Showcase — DisplayCards (RESTORED FROM ORBITAL) */}
            <div ref={displayCardsRef} className="relative w-full min-h-[500px] sm:min-h-[550px] mt-12 flex items-center justify-center">
              <div className="w-full max-w-4xl px-4 flex justify-center text-center">
                {displayCardsInView ? (
                  <DisplayCards
                    cards={[
                      {
                        icon: <ShieldPulse isMobile={isMobile} />,
                        title: "Protection",
                        description: "100% Secure Interactions",
                        date: "Enabled",
                        iconClassName: "text-primary",
                        titleClassName: "text-primary",
                        className: "[grid-area:stack] hover:-translate-y-12 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
                      },
                      {
                        icon: <SparklinePeak isMobile={isMobile} />,
                        title: "Engagement",
                        description: "Maximized Retention Rates",
                        date: "Real-time",
                        iconClassName: "text-primary",
                        titleClassName: "text-primary",
                        className: "[grid-area:stack] translate-x-8 translate-y-12 hover:-translate-y-4 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
                      },
                      {
                        icon: <DeviceToggle isMobile={isMobile} />,
                        title: "Analytics",
                        description: "Deep Traffic Insights",
                        date: "Live Data",
                        iconClassName: "text-primary",
                        titleClassName: "text-primary",
                        className: "[grid-area:stack] translate-x-16 translate-y-24 hover:-translate-y-2 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
                      },
                      {
                        icon: <RadarPing isMobile={isMobile} />,
                        title: "Geo Maps",
                        description: "Global Interactive Tracking",
                        date: "Global",
                        iconClassName: "text-primary",
                        titleClassName: "text-primary",
                        className: "[grid-area:stack] translate-x-24 translate-y-36 hover:translate-y-4 before:absolute before:w-[100%] before:outline-1 before:rounded-xl before:outline-border before:h-[100%] before:content-[''] before:bg-blend-overlay before:bg-background/50 grayscale-[100%] hover:before:opacity-0 before:transition-opacity before:duration-700 hover:grayscale-0 before:left-0 before:top-0",
                      },
                      {
                        icon: <Globe className="w-5 h-5 text-primary" />,
                        title: "Global Reach",
                        description: "Ultra-low Latency Network",
                        date: "Anywhere",
                        iconClassName: "text-primary",
                        titleClassName: "text-primary",
                        className: "[grid-area:stack] translate-x-32 translate-y-[12rem] hover:translate-y-16",
                      },
                    ]}
                  />
                ) : (
                  <div className="h-[400px] w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />
                )}
              </div>
            </div>
          </div>
        </div>
      </section >

      <div ref={howItWorksRef} className="w-full relative" style={{ position: 'relative' }}>
        {howItWorksInView ? (
          <StackedHowItWorks />
        ) : (
          <div className="h-96 w-full animate-pulse bg-white/[0.02] border border-white/5 rounded-3xl" />
        )}
      </div>

      {/* ── Pricing ── */}
      <section id="pricing" className="py-2 md:py-20 border-t border-white/5 relative z-10 overflow-hidden">
        {/* Ambient Glows */}
        <div className="absolute top-0 right-[-10%] w-[50vw] h-[50vw] bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.07),transparent_70%)] blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-[-10%] w-[40vw] h-[40vw] bg-[radial-gradient(circle_at_center,rgba(0,64,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12">
          <div className="mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold tracking-tighter text-white mb-4 uppercase">Clear pricing.</h2>
            <p className="text-xl text-white/50 max-w-xl font-light">No complex tiers. Full access, straight out of the box.</p>
          </div>

          <div className="flex flex-col gap-6">
            {[
              {
                name: 'Starter',
                planId: 'free',
                price: 'Free',
                target: 'Core Features',
                features: ['50 Links & 20 QR Codes', '1 Bio Page', '1 Product Store (2 Products)', '5 Forms & 5 Polls', '5 Referral Trackers', 'Up to 2 Team Members', '1 Developer API Key', '7-Day Analytics'],
                href: '/auth/sign-up',
                highlight: false,
              },
              {
                name: 'Pro',
                planId: 'pro',
                price: isSouthAsia ? `₹${PRICING_CONFIG.pro.INR.monthly}/mo` : `$${PRICING_CONFIG.pro.USD.monthly}/mo`,
                target: 'Maximum Growth & Branding',
                features: ['50 Links/mo & 50 QR Codes/mo', '10 Developer API Keys', '5 Bio Pages', '2 Product Stores (10 Products/mo)', '10 Forms/mo & 10 Polls/mo', '10 Referral Trackers/mo', 'Up to 4 Team Members', 'Unlimited Analytics', '3 Teams'],
                href: session ? '/dashboard' : '/auth/sign-up',
                ctaOverride: 'Upgrade to Pro',
                highlight: true,
              },
              {
                name: 'Premium',
                planId: 'premium',
                price: isSouthAsia ? `₹${PRICING_CONFIG.premium.INR.monthly}/mo` : `$${PRICING_CONFIG.premium.USD.monthly}/mo`,
                target: 'Enterprise & Agencies',
                features: ['Unlimited Links & QR Codes', '100 Developer API Keys', '5 Product Stores', 'Unlimited Forms & Polls', 'Unlimited Referral Trackers', 'Up to 10 Team Members', 'Custom Domains', '5 Teams & Unltd. Log History'],
                href: session ? '/dashboard' : '/auth/sign-up',
                ctaOverride: 'Upgrade to Premium',
                highlight: false,
              },
            ].map((plan, idx) => (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className="relative h-full rounded-3xl"
              >
                <GlowingEffect
                  spread={60}
                  glow={true}
                  disabled={false}
                  proximity={64}
                  inactiveZone={0.01}
                  borderWidth={3}
                  variant="blue"
                />
                <div
                  className={`relative h-full sharp-edge rounded-3xl p-6 lg:p-10 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 group bg-background/50 backdrop-blur-sm transition-colors ${plan.highlight ? 'border-primary/40 bg-primary/[0.02] shadow-[0_0_60px_rgba(0,102,255,0.05)]' : 'border-white/5'}`}
                >
                  <div className="flex-1">
                    {plan.highlight && (
                      <span className="inline-block px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full mb-3">Most Beneficial</span>
                    )}
                    <p className="text-white/40 font-mono text-sm tracking-widest uppercase mb-2">{plan.target}</p>
                    <h3 className="text-3xl font-bold text-white">{plan.name}</h3>
                  </div>
                  <div className="flex-1 flex flex-wrap gap-x-6 gap-y-2">
                    {plan.features.map(f => (
                      <span key={f} className="text-white/60 text-sm flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0" />
                        {f}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                    <p className="text-4xl font-bold text-white tracking-tighter">{plan.price}</p>
                    {plan.href ? (
                      session ? (
                        <Link
                          id={`pricing-plan-${plan.planId}-btn`}
                          href={plan.href}
                          className={`px-8 py-4 rounded-full font-bold transition-all hover:-translate-y-1 active:scale-95 shadow-lg ${userPlan === plan.planId
                            ? 'bg-primary/20 text-primary/40 cursor-not-allowed pointer-events-none'
                            : (userPlan && (userPlan === 'pro' || userPlan === 'premium') && plan.planId === 'free')
                              ? 'bg-white/5 text-white/20 cursor-not-allowed pointer-events-none'
                              : 'bg-white text-black'
                            }`}
                        >
                          {userPlan === plan.planId ? 'Your Plan' : (plan.planId !== 'free' ? 'Upgrade' : 'Go to Dashboard')}
                        </Link>
                      ) : (
                        <Link id={`pricing-plan-${plan.planId}-btn`} href={plan.href} className="px-8 py-4 rounded-full font-bold transition-all hover:scale-105 active:scale-95 bg-white text-black">
                          {plan.ctaOverride || 'Get Started Free'}
                        </Link>
                      )
                    ) : (
                      <button id={`pricing-plan-${plan.planId}-btn`} className="px-8 py-4 rounded-full font-bold bg-primary text-white shadow-[0_0_20px_rgba(0,102,255,0.3)] hover:scale-105 active:scale-95 transition-all">
                        Coming Soon
                      </button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-16 text-center"
          >
            <Link id="pricing-compare-btn" href="/pricing" className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-white/70 bg-white/5 border border-white/10 hover:bg-white/10 hover:text-white transition-all text-sm font-semibold tracking-widest uppercase hover:scale-105 active:scale-95">
              Compare All Features
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section >

      {/* ── FAQ Section ── */}
      <section className="py-24 border-t border-white/5 relative z-10">
        <div className="max-w-[900px] mx-auto px-6 relative">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter text-white">
              Frequently Asked <span className="text-gray-500">Questions</span>
            </h2>
            <p className="text-sm sm:text-base text-white/50 max-w-md mx-auto font-light">
              Find answers to common questions about our link shortening, bio pages, dynamic QR codes, storefronts, and marketing features.
            </p>
          </div>

          <div className="space-y-4">
            {[
              {
                question: "What is Lenklyst?",
                answer: "Lenklyst is an all-in-one branding and marketing ecosystem designed for creators, startups, marketers, and businesses. It integrates link shortening, customizable link-in-bio landing pages, dynamic QR codes, digital product storefronts, forms and surveys, and automated viral referral program software into a single unified platform."
              },
              {
                question: "How does Reel-to-Link work with my Link in Bio?",
                answer: "Assign a short #code (like #24) or video thumbnail to any link on your bio page. When followers view your video, tell them to tap your bio link and enter the code to open the exact link in one tap."
              },
              {
                question: "How do dynamic QR codes work on Lenklyst?",
                answer: "Unlike static QR codes, dynamic QR codes store a redirect link. This means you can update the destination URL of your QR code at any time directly in your Lenklyst dashboard, even after you've printed it. It also allows you to track detailed scan analytics, including time, location, referrers, and device types."
              },
              {
                question: "What types of forms can I build with Lenklyst?",
                answer: "Lenklyst allows you to create highly customizable online forms, quizzes, and viral polls. You can configure multiple choice questions, ratings fields, and email capture blocks. All responses are logged instantly and summarized in your dashboard with visual charts."
              },
              {
                question: "What are the fees for selling digital products?",
                answer: "We charge an ultra-low platform fee of just 5% per transaction on digital product stores, allowing you to keep 95% of your gross earnings (excluding standard payment processing costs). This is significantly lower than alternative platforms like Gumroad which charge up to 10% platform cuts."
              },
              {
                question: "How does the referral marketing system help my business?",
                answer: "Our referral marketing software allows you to turn your customers and followers into active brand advocates. You can generate custom referral invite links, track milestone scores, set rewards (such as coupons, discounts, or downloads), and automate viral sharing loops that drive organic signups."
              },
              {
                question: "Is there a free trial or free tier available?",
                answer: "Absolutely. Lenklyst offers a fully featured free-tier plan that includes 50 smart links, 20 dynamic QR codes, 1 link-in-bio page, and access to store/form/referral tools so you can test and launch your campaigns without entering any credit card details."
              }
            ].map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div
                  key={idx}
                  className="bg-white/[0.01] hover:bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden transition-all duration-300"
                >
                  <button
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
                  >
                    <span className="text-sm font-bold uppercase tracking-tight text-white/90">
                      {faq.question}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-white/40 transition-transform duration-300 ${
                        isOpen ? "rotate-180 text-primary" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="px-5 pb-5 pt-1 text-xs sm:text-sm text-white/50 leading-relaxed font-light border-t border-white/[0.02]">
                          {faq.answer}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-32 border-t border-white/5 relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.06),transparent_70%)] blur-[150px] pointer-events-none" />
        <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter text-white uppercase">
              Own your<br /><span className="text-primary">online presence.</span>
            </h2>
            <p className="text-xl text-white/40 max-w-xl mx-auto font-light">
              Be among the first creators and marketers to make Lenklyst your growth engine.
            </p>
            <Link
              id="cta-bottom-btn"
              href={session ? '/dashboard' : '/auth/sign-up'}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 sm:gap-3 px-6 py-4 sm:px-10 sm:py-5 bg-primary text-white rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-primary/90 transition-all hover:scale-105 active:scale-95 shadow-[0_0_60px_rgba(0,102,255,0.4)]"
            >
              <span className="text-center">{session ? 'Open Dashboard' : 'Start for Free'}</span>
              <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── Marquee Section with Globe Background ── */}
      <div ref={globeRef} className="w-full relative min-h-[32rem]">
        {globeInView ? (
          <GlobeDemo>
            <div className="relative z-50 flex justify-center items-center py-10 md:py-8">
              <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 flex justify-center">
                <TextMarquee
                  prefix={<span className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-white/20 mr-4">Lenklyst for</span>}
                  height={32}
                  itemHeight={32}
                  speed={2.5}
                  className="text-xs sm:text-sm font-bold tracking-[0.2em] uppercase text-primary/60"
                >
                  <span>Marketers</span>
                  <span>Creators</span>
                  <span>Founders</span>
                  <span>Influencers</span>
                  <span>Businesses</span>
                </TextMarquee>
              </div>
            </div>
          </GlobeDemo>
        ) : (
          <div className="h-[32rem] w-full animate-pulse bg-transparent flex justify-center items-center py-10 md:py-8">
            <div className="max-w-[1400px] 2xl:max-w-[1800px] mx-auto px-6 lg:px-12 flex justify-center">
               <div className="w-[300px] h-8 bg-white/5 rounded-lg"></div>
            </div>
          </div>
        )}
      </div>

      </main>

      <MarketingFooter />
    </div>
  )
}
